import { ILocationAlertSummaryResponse } from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQueryResult,
    DataSourceRequest,
    DataSourceSort,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';

const defaultSort: DataSourceSort[] = [
    { field: 'TotalAssetsInCriticalAlarmCount', dir: 'desc' },
];

export type CorporationAssetsWithAlertsFilter = Readonly<{
    corporationId: string;
    locationIds?: string[];
}>;

export type UseAssetsWithAlertsDataSourceOptions = BaseDataSourceOptions<
    ILocationAlertSummaryResponse,
    CorporationAssetsWithAlertsFilter
>;

type SystemAlertCounts = {
    AssetsInAlarmCount: number;
    AssetsInCriticalAlarmCount: number;
    AssetsInWarningCount: number;
};

type LocationAlertCounts = {
    TotalAssetsInAlarmCount: number;
    TotalAssetsInCriticalAlarmCount: number;
    TotalAssetsInWarningCount: number;
};

type HasAlertCounts = Partial<SystemAlertCounts & LocationAlertCounts>;

function hasNonZeroCount(counts: Array<number | undefined>): boolean {
    return counts.some((count) => (count ?? 0) > 0);
}

function hasAlerts(entity: HasAlertCounts): boolean {
    const {
        AssetsInAlarmCount,
        AssetsInCriticalAlarmCount,
        AssetsInWarningCount,
        TotalAssetsInAlarmCount,
        TotalAssetsInCriticalAlarmCount,
        TotalAssetsInWarningCount,
    } = entity;

    // If any system-level counts are defined, check those
    if (
        [AssetsInAlarmCount, AssetsInCriticalAlarmCount, AssetsInWarningCount].some(
            (count) => typeof count === 'number',
        )
    ) {
        return hasNonZeroCount([
            AssetsInAlarmCount,
            AssetsInCriticalAlarmCount,
            AssetsInWarningCount,
        ]);
    }

    // Otherwise check location-level counts
    return hasNonZeroCount([
        TotalAssetsInAlarmCount,
        TotalAssetsInCriticalAlarmCount,
        TotalAssetsInWarningCount,
    ]);
}

async function getCorporationAssetsWithAlerts({
    filter,
    sort,
    page,
    pageLimit,
}: DataSourceRequest<CorporationAssetsWithAlertsFilter>): Promise<
    DataSourceQueryResult<ILocationAlertSummaryResponse>
> {
    const result = await sdClient.getLocationAlertSummariesForCorporation({
        corporationId: filter.corporationId,
        filter: filter.locationIds ? Filter.in('Id', filter.locationIds) : undefined,
        page,
        pageLimit,
        orderBy: buildOrderBy(sort),
    });

    // Filter out locations with no alerts and update their systems
    const filteredResult = {
        ...result,
        Result: result.Result.filter(hasAlerts).map((location) => ({
            ...location,
            Systems: location.Systems.filter(hasAlerts),
        })),
    };

    return toDataSourceResult(filteredResult);
}

export type CorporationAssetsWithAlertsInfiniteDataSource = InfiniteDataSource<
    ILocationAlertSummaryResponse,
    CorporationAssetsWithAlertsFilter
>;

export function useInfiniteCorporationAssetsWithAlertsDataSource(
    options: UseAssetsWithAlertsDataSourceOptions,
): CorporationAssetsWithAlertsInfiniteDataSource {
    return useInfiniteDataSource({
        defaultSort,
        ...options,
        query: getCorporationAssetsWithAlerts,
    });
}
